import { removeUndefinedFields } from 'lib/helpers';
import { TemplateData, MadlibDataType } from 'lib/types/madlib';
import { isFileUploadQuestionInputValue } from 'lib/utils/madlib';
import lodash, { isEmpty } from 'lodash';
import { logAndCaptureMessage } from 'utils';
import { reuploadNoticeFileContent } from 'utils/duplicateNotice';

// Get the madlib file object from the madlibData of notice
const getMadlibFileFromMadlibData = (
  originalNoticeMadlibQuestions: TemplateData
) => {
  if (!originalNoticeMadlibQuestions) return;

  const oldFileData = lodash.pickBy(
    { ...originalNoticeMadlibQuestions },
    isFileUploadQuestionInputValue
  );

  if (!oldFileData || isEmpty(oldFileData)) return null;
  const oldFileDataObj = Object.values(oldFileData)[0];
  const oldFileDataKey = Object.keys(oldFileData)[0];

  return { key: oldFileDataKey, value: oldFileDataObj };
};

/** Get the updated madlib file uploaded data, determine if current draft id exists in file path,
 * if exists we will not reupload the file content; if not the file content reuploaded
 */
export const getNewMadlibFileData = async (
  originalNoticeMadlibQuestions: MadlibDataType,
  originalNoticeId: string,
  draftNoticeId: string,
  isDuplicated?: boolean
) => {
  if (isEmpty(originalNoticeMadlibQuestions.questionTemplateData)) return;
  const oldFileData = getMadlibFileFromMadlibData(
    originalNoticeMadlibQuestions.questionTemplateData
  );
  if (!oldFileData) return;

  if (oldFileData.value.storagePath.includes(draftNoticeId)) return;
  const uploadLocation = `documentcloud/madlib-notices-uploads/${draftNoticeId}`;

  const duplicatePrefix = isDuplicated ? 'duplicated_' : '';
  const newFileData = await reuploadNoticeFileContent(
    oldFileData.value,
    uploadLocation,
    duplicatePrefix
  );

  if (!newFileData) {
    logAndCaptureMessage('Unable to update madlib notice file content', {
      draftNoticeId,
      originalNoticeId,
      noticeFileName: oldFileData.value.sanitizedFileName
    });
    return;
  }

  const newDownloadUrl: string = await newFileData.uploadRef.getDownloadURL();
  const fileData = {
    sanitizedFileName: newFileData.file.name,
    storagePath: newFileData.uploadRef.fullPath,
    linkToUploadedFile: newDownloadUrl
  };
  const updatedQuestionsTemplate = {
    ...originalNoticeMadlibQuestions.questionTemplateData,
    [oldFileData.key]: fileData
  };
  const newMadlib = removeUndefinedFields({
    ...originalNoticeMadlibQuestions,
    questionTemplateData: updatedQuestionsTemplate
  });
  return newMadlib;
};
